import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";

import storage from "redux-persist/lib/storage";
import attributesSlice, { AttributesState } from "./slices/attributesSlice";
import { AuthState, authSlice } from "./slices/authSlice";
import clientSettingsSlice, {
  ClientSettingsState,
} from "./slices/clientSettingsSlice";
import featuresSlice, { FeaturesState } from "./slices/featuresSlice";
import formReducer from "./slices/formSlice";
import interactiveListSlice, {
  InteractiveListState,
} from "./slices/interactiveListSlice";
import { LookupListState, lookupListSlice } from "./slices/lookupListSlice";
import { SystemState, systemSlice } from "./slices/systemSlice";
import widgetsSlice, { WidgetsState } from "./slices/widgetSlice";
import assetFiltersSlice, {
  AssetFiltersState,
} from "./slices/assetFiltersSlice";
import reservationFiltersSlice, {
  ReservationFiltersState,
} from "./slices/reservationFiltersSlice";
import commonFiltersSlice, {
  CommonFiltersState,
} from "./slices/commonFiltersSlice";
import locationsSlice, { LocationDataState } from "./slices/locationsSlice";
import paymentMethodTypeSlice, {
  PaymentMethodTypeState,
} from "./slices/paymentMethodTypeSlice";
import DocumentSlice, { DocumentsState } from "./slices/documentSlice";
import clientFieldSettingsReducer, { ClientFieldSettingsState } from "./slices/clientFieldSettingsSlice";

// Define the root state
export interface RootState {
  auth: AuthState;
  system: SystemState;
  lookupList: LookupListState;
  widgets: WidgetsState;
  attributes: AttributesState;
  interactiveList: InteractiveListState;
  clientSettings: ClientSettingsState;
  features: FeaturesState;
  form: ReturnType<typeof formReducer>;
  assetFilters: AssetFiltersState;
  reservationFilters: ReservationFiltersState;
  commonFilters: CommonFiltersState;
  locationData: LocationDataState;
  paymentMethodType: PaymentMethodTypeState;
  documents: DocumentsState;
  clientFieldSettings: ClientFieldSettingsState;
}

// Create a root reducer combining all slices
const rootReducer = combineReducers<RootState>({
  auth: authSlice.reducer,
  system: systemSlice.reducer,
  lookupList: lookupListSlice.reducer,
  widgets: widgetsSlice,
  attributes: attributesSlice,
  interactiveList: interactiveListSlice,
  clientSettings: clientSettingsSlice,
  features: featuresSlice,
  form: formReducer,
  assetFilters: assetFiltersSlice,
  reservationFilters: reservationFiltersSlice,
  commonFilters: commonFiltersSlice,
  locationData: locationsSlice,
  paymentMethodType: paymentMethodTypeSlice,
  documents: DocumentSlice,
  clientFieldSettings: clientFieldSettingsReducer,
});

// Configure persist options
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "clientSettings", "features", "clientFieldSettings"],
};

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

// Create the Redux store
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export default store;

// Infer the `AppDispatch` type from the store itself
export type AppDispatch = typeof store.dispatch;
